import type { AxiosInstance } from 'axios';
import { t } from 'i18next';
import type { QuestionTypeMapping } from '@/models/questionType';

export type CreateLinkedQuestion = {
   questionChoiceId: string;
   questionTypesMapping: QuestionTypeMapping;
};

export type UpdateRightChoice = {
   questionChoiceId: string;
   isRightChoice: boolean;
};

export type UpdateChoiceImage = {
   choiceId: string;
   file: File;
};

class QuestionChoiceService {
   private provider: AxiosInstance;

   constructor(provider: AxiosInstance) {
      this.provider = provider;
   }

   async createLinkedQuestion({
      questionChoiceId,
      questionTypesMapping,
   }: CreateLinkedQuestion) {
      if (!questionTypesMapping?.checkbox) {
         throw new Error('Missing questionTypesMapping.checkbox');
      }

      await this.provider.post('rest/question-choices/mutate', {
         mutate: [
            {
               operation: 'update',
               key: questionChoiceId,
               relations: {
                  questions: [
                     {
                        operation: 'create',
                        attributes: {
                           question_type_id: questionTypesMapping.checkbox,
                           label: t('survey.linkedQuestion'),
                           is_required: false,
                           is_visible: true,
                           order: 1,
                        },
                        relations: {
                           questionChoices: [
                              {
                                 operation: 'create',
                                 attributes: {
                                    label: t('choice.new'),
                                    order: 1,
                                 },
                              },
                              {
                                 operation: 'create',
                                 attributes: {
                                    label: t('choice.new'),
                                    order: 2,
                                 },
                              },
                           ],
                        },
                     },
                  ],
               },
            },
         ],
      });
   }

   async duplicateQuestionChoice(questionChoiceId: string) {
      await this.provider.post('rest/question-choices/actions/duplicate', {
         fields: [
            {
               name: 'question_choice_id',
               value: questionChoiceId,
            },
         ],
      });
   }

   async deleteQuestionChoice(questionChoiceId: string) {
      await this.provider.delete('rest/question-choices', {
         data: {
            resources: [questionChoiceId],
         },
      });
   }

   async updateRightChoice({
      questionChoiceId,
      isRightChoice,
   }: UpdateRightChoice) {
      await this.provider.post('rest/question-choices/mutate', {
         mutate: [
            {
               operation: 'update',
               key: questionChoiceId,
               attributes: {
                  is_right_choice: isRightChoice,
               },
            },
         ],
      });
   }

   async updateChoiceImage({
      choiceId,
      file,
   }: UpdateChoiceImage): Promise<void> {
      const formData = new FormData();

      formData.append('mutate[0][operation]', 'update');
      formData.append('mutate[0][key]', choiceId);
      formData.append('mutate[0][media][0][file]', file);
      formData.append('mutate[0][media][0][collection_name]', 'question');

      await this.provider.post('rest/question-choices/mutate', formData, {
         headers: {
            'Content-Type': 'multipart/form-data',
         },
      });
   }
}

export default QuestionChoiceService;
